import { ReactElement } from 'react';
import { TEST_IDS } from '@localstack/constants';
import { useParams } from 'react-router-dom';
import { Card, CardActions, CardContent } from '@mui/material';
import { useAwsEffect, useAwsGetter, useRoutes, useSnackbar } from '@localstack/services';
import { CreateWebFunctionEndpointRequest, UpdateWebFunctionEndpointRequest } from '@localstack/types';
import { LambdaWebEndpointForm, LoadingFragment, PageTitle, ProgressButton } from '@localstack/ui';

import { LambdaProps } from './types';
import { LambdaWebVersionGate } from './components/LambdaWebVersionGate';
import { WebFunctionTabs } from './components/WebFunctionTabs';
import { getWebEndpointRoutingError } from './utils/webEndpointRouting';

const FORM_ID = 'LambdaWebEndpoint';
export const LambdaWebEndpointUpsert = (props: LambdaProps): ReactElement => (
  <LambdaWebVersionGate {...props}>
    <LambdaWebEndpointUpsertContent {...props} />
  </LambdaWebVersionGate>
);

const LambdaWebEndpointUpsertContent = ({ Layout, clientOverrides, routes }: LambdaProps): ReactElement => {
  const { name = '', endpointName } = useParams<'name' | 'endpointName'>();
  const { goto } = useRoutes();
  const { showSnackbar } = useSnackbar();
  const { data: endpoint, isLoading: fetching } = useAwsGetter(
    'LambdaWeb',
    'getWebFunctionEndpoint',
    [{ functionName: name, endpointName: endpointName ?? '' }],
    { clientOverrides, enable: !!endpointName },
  );
  const { createWebFunctionEndpoint, updateWebFunctionEndpoint, isLoading } = useAwsEffect(
    'LambdaWeb',
    ['createWebFunctionEndpoint', 'updateWebFunctionEndpoint'],
    {
      clientOverrides,
      revalidate: ['listWebFunctionEndpoints', 'getWebFunctionEndpoint'],
    },
  );
  const prepareRouting = <T extends UpdateWebFunctionEndpointRequest>(data: T) => {
    const revisionWeights =
      data.autoDeploymentMode === 'Disabled'
        ? (data.revisionWeights?.map((entry) => ({ ...entry, weight: Number(entry.weight) })) ?? [])
        : undefined;
    // MagicForm renders the model's list, but cannot validate cross-row totals or uniqueness.
    const error = getWebEndpointRoutingError(data.autoDeploymentMode, revisionWeights ?? []);
    if (error) {
      showSnackbar({ severity: 'error', message: error });
      return undefined;
    }
    return { ...data, functionName: name, revisionWeights };
  };
  const handleCreate = async (data: CreateWebFunctionEndpointRequest) => {
    const request = prepareRouting(data);
    if (!request) return;
    const result = await createWebFunctionEndpoint(request);
    if (result?.endpointName) goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINT, { name, endpointName: result.endpointName });
  };
  const handleUpdate = async (data: UpdateWebFunctionEndpointRequest) => {
    const request = prepareRouting(data);
    if (!request || !endpointName) return;
    if (await updateWebFunctionEndpoint({ ...request, endpointName })) {
      goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINT, { name, endpointName });
    }
  };
  const title = endpointName ? 'Update Endpoint' : 'Create Endpoint';
  return (
    <Layout
      documentTitle={`Lambda: ${title}`}
      tabs={<WebFunctionTabs routes={routes} functionName={name} clientOverrides={clientOverrides} />}
      title={
        <PageTitle
          title={title}
          breadcrumbs={[
            ['Lambda', () => goto(routes.RESOURCES_LAMBDA)],
            ['Web Functions', () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTIONS)],
            [name, () => goto(routes.RESOURCES_LAMBDA_WEB_FUNCTION, { name })],
            ['Endpoints', () => goto(routes.RESOURCES_LAMBDA_WEB_ENDPOINTS, { name })],
            [title, null],
          ]}
        />
      }
      actions={
        <ProgressButton form={FORM_ID} type="submit" variant="outlined" color="primary" loading={isLoading}>
          Submit
        </ProgressButton>
      }
    >
      <Card>
        <CardContent>
          <LoadingFragment loading={!!endpointName && fetching}>
            {(!endpointName || endpoint) && (
              <LambdaWebEndpointForm
                formId={FORM_ID}
                functionName={name}
                endpoint={endpoint ?? undefined}
                loading={isLoading}
                clientOverrides={clientOverrides}
                onCreate={handleCreate}
                onUpdate={handleUpdate}
              />
            )}
          </LoadingFragment>
        </CardContent>
        <CardActions>
          <ProgressButton
            data-testid={TEST_IDS.LAMBDA_WEB_FORM_SUBMIT}
            form={FORM_ID}
            type="submit"
            variant="contained"
            color="primary"
            loading={isLoading}
          >
            Submit
          </ProgressButton>
        </CardActions>
      </Card>
    </Layout>
  );
};
